/**
 * 용량 룰 검수 화면의 react-query 훅.
 *
 * react-query는 **v3(구 `react-query` 패키지)**다 — 선례 `useGroundTruths.ts` 문법을 따른다
 * (`@tanstack/react-query` 아님). 성공 판정은 `error === 0`을 queryFn/mutationFn 안에서
 * 한 곳에서 수행하고, 실패면 throw해 react-query의 isError/onError로 흘린다.
 *
 * 무효화 키(계획 D5): 목록 `['doseRules']`, 상세 `['doseRule']`, 집계 `['doseRuleStats']`,
 * 라벨 `['drugProductLabel', itemCode]`, 버전 목록 `['doseRuleVersions', ruleId]`,
 * 노트 `['doseRuleNotes', ruleId]`.
 *
 * 버전·노트 키는 상세 키의 **형제**다(`['doseRule', ruleId, 'versions']`처럼 중첩하지 않는다).
 * react-query v3의 키 매칭은 배열 원소 단위라 기존 `['doseRule']` 무효화가 이 둘을 건드리지
 * 않고, 그래서 `invalidateAll()`에 **명시적으로** 넣어야 한다. 중첩하면 무효화가 암묵적이 되어
 * 단정 지점이 사라지고, 버전 절이 상세와 독립적으로 살아야 한다는 설계와 반대 신호를 준다
 * (룰 버전 Phase 1 계획 Q-1).
 */
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ToastManager } from "components/organisms/CommonUI";
import { fetchDrugLabelByItemCode } from "api/drugService";
import { doseRuleAdminApi } from "services/api/doseRules/doseRuleAdminApi";
import {
  AdminDoseRuleDetail,
  AdminDoseRuleListData,
  AdminDoseRuleNote,
  AdminDoseRuleStats,
  AdminDoseRuleVersionListItem,
  FindDoseRulesQuery,
  FromStep,
  ReextractResult,
  UpdateDoseRuleStatusBody,
  UpdateDoseRuleStatusResult,
} from "services/api/doseRules/types";
import { reextractReasonText } from "pages/Admin/DoseRules/reextractConfirm";

/** onError 토스트 문안: 서버 message > Error.message > 기본 문안. */
const errorMessage = (error: unknown, fallback: string): string => {
  const anyErr = error as { response?: { data?: { message?: string } }; message?: string };
  return anyErr?.response?.data?.message || anyErr?.message || fallback;
};

/**
 * 재추출 오류 형상 보존(spec frontend 2). 현행 `throw new Error(message)`는 봉투의
 * `error`(코드)와 `data`(6112 사유)를 잃는다. onError에서 6102·6112를 코드로 분기하고
 * 6112 사유를 프런트 매핑으로 토스트하기 위해 코드·데이터를 담아 던진다.
 */
class ReextractError extends Error {
  code: number;
  data?: { unavailableReason?: string } | null;
  constructor(code: number, message?: string, data?: unknown) {
    super(message || "재추출 요청에 실패했습니다.");
    this.name = "ReextractError";
    this.code = code;
    this.data = (data as { unavailableReason?: string }) ?? null;
  }
}

/** 목록 조회. keepPreviousData로 페이지 이동 시 깜빡임 완화. */
export const useDoseRules = (query: FindDoseRulesQuery) => {
  return useQuery<AdminDoseRuleListData>({
    queryKey: ["doseRules", query],
    queryFn: async () => {
      const res = await doseRuleAdminApi.list(query);
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "룰 목록을 불러오지 못했습니다.");
      }
      return res.data;
    },
    keepPreviousData: true,
    staleTime: 60 * 1000,
  });
};

/** 집계 조회. 필터 비의존(전역 카운트) — 필터 변경에 재조회되지 않는다. */
export const useDoseRuleStats = () => {
  return useQuery<AdminDoseRuleStats>({
    queryKey: ["doseRuleStats"],
    queryFn: async () => {
      const res = await doseRuleAdminApi.stats();
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "집계를 불러오지 못했습니다.");
      }
      return res.data;
    },
    staleTime: 60 * 1000,
  });
};

/**
 * 상세 조회. ruleId 없으면 비활성.
 *
 * **`staleTime: 0`**(D-17) — 버전·노트 쿼리와 같은 정책이다. 상세 응답의 `step2Snapshot`은
 * `룰만 재추출` 가용성을 담는데, `Step 2부터` 잡이 끝난 뒤 앱 안에서 다시 들어와도 전역 5분
 * 캐시가 옛 `missing` 상태를 보여 준다. 6112 응답 뒤에도 무효화만으로는 신선을 보장하지 못한다.
 */
export const useDoseRuleDetail = (ruleId?: string) => {
  return useQuery<AdminDoseRuleDetail>({
    queryKey: ["doseRule", ruleId],
    queryFn: async () => {
      const res = await doseRuleAdminApi.detail(ruleId as string);
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "룰 상세를 불러오지 못했습니다.");
      }
      return res.data;
    },
    enabled: !!ruleId,
    staleTime: 0,
  });
};

/**
 * 버전 목록 조회(룰 버전 Phase 1). ruleId 없으면 비활성.
 *
 * **`staleTime: 0`** — 전역 기본 5분(`src/api/queryClient.ts:11`)을 이 쿼리만 끈다(보정 7h).
 * 화면 안에서 버전을 만드는 유일한 트리거인 재추출은 **잡만 큐잉**하므로(설계 결정 17) 버튼을
 * 누른 시점의 무효화로는 새 버전이 보일 수 없다 — 새 버전은 잡 워커가 나중에 만든다. 그 버전이
 * 약사에게 닿는 경로는 "상세를 다시 열 때 다시 묻는 것"뿐이고, 전역 5분 캐시가 그 재방문을
 * 정확히 가로막는다. 같은 화면에 머무는 동안은 여전히 자동 갱신하지 않는다
 * (`refetchOnWindowFocus: false`).
 *
 * 전역 `retry: false`라 일시적 실패도 즉시 오류 상태가 되고, 그래서 절에 수동 `다시 시도`를
 * 둔다(계획 H-3·I-1·I-2).
 */
export const useDoseRuleVersions = (ruleId?: string) => {
  return useQuery<AdminDoseRuleVersionListItem[]>({
    queryKey: ["doseRuleVersions", ruleId],
    queryFn: async () => {
      const res = await doseRuleAdminApi.versions(ruleId as string);
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "룰 버전을 불러오지 못했습니다.");
      }
      return res.data;
    },
    enabled: !!ruleId,
    staleTime: 0,
  });
};

/**
 * 검수 메모·시스템 노트 조회(룰 버전 Phase 1).
 *
 * `hasHistory`는 **호출부가 버전 목록 응답 길이로 계산해 넘긴다**(`versions.length > 1`).
 * 훅이 다른 쿼리를 내부에서 읽으면 호출 순서 의존이 생기고, 이 repo의 훅은 전부 인자→쿼리
 * 1:1이다(계획 A-1). 버전이 하나뿐인 룰엔 은퇴 사유가 존재할 수 없어 요청 자체를 내지 않는다.
 *
 * **`staleTime: 0`** — 근거는 `useDoseRuleVersions`와 같다(보정 7h). 버전 줄과 그 줄의 사유가
 * 서로 다른 시점을 가리키면 "사유가 사라진 버전"이 만들어져 읽힌다.
 */
export const useDoseRuleNotes = (ruleId?: string, hasHistory?: boolean) => {
  return useQuery<AdminDoseRuleNote[]>({
    queryKey: ["doseRuleNotes", ruleId],
    queryFn: async () => {
      const res = await doseRuleAdminApi.notes(ruleId as string);
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "은퇴 사유를 불러오지 못했습니다.");
      }
      return res.data;
    },
    enabled: !!ruleId && !!hasHistory,
    staleTime: 0,
  });
};

/**
 * 허가사항 원문(labelInfo) 조회. 상세 오른쪽 패널 데이터 소스(계획 D1).
 * 계약 5종 밖의 기존 프론트 API를 재사용한다. itemCode 없으면 비활성.
 */
export const useDrugProductLabel = (itemCode?: string | null) => {
  return useQuery({
    queryKey: ["drugProductLabel", itemCode],
    queryFn: () => fetchDrugLabelByItemCode(itemCode as string),
    enabled: !!itemCode,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * 상태 전이·재추출 mutation. 성공 시 목록·상세·집계 캐시를 모두 무효화하고
 * 사용자에게 토스트로 알린다(승인 시 cascaded, 재추출 시 jobId).
 */
export const useDoseRuleMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["doseRules"] });
    queryClient.invalidateQueries({ queryKey: ["doseRule"] });
    queryClient.invalidateQueries({ queryKey: ["doseRuleStats"] });
    // 버전·노트는 **공용 함수에** 넣는다 — 이유는 **승인·반려 후 재방문 대비**다(설계 결정 9).
    //
    // 재추출 경로에는 효과가 없다(보정 7h 주석 정정). 재추출 버튼은 **잡만 큐잉**하므로
    // (설계 결정 17) 무효화가 도는 시점엔 새 버전이 아직 없고, 오히려 큐잉 시점의 재조회가
    // 옛 목록을 신선한 캐시로 다시 저장한다. 잡이 만든 새 버전이 보이는 경로는 두 쿼리의
    // `staleTime: 0`이지 이 무효화가 아니다 — 여기에 재추출 근거를 적지 않는다.
    queryClient.invalidateQueries({ queryKey: ["doseRuleVersions"] });
    queryClient.invalidateQueries({ queryKey: ["doseRuleNotes"] });
  };

  const updateStatus = useMutation<
    UpdateDoseRuleStatusResult,
    unknown,
    { ruleId: string; body: UpdateDoseRuleStatusBody }
  >({
    mutationFn: async ({ ruleId, body }) => {
      const res = await doseRuleAdminApi.updateStatus(ruleId, body);
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "상태 변경에 실패했습니다.");
      }
      return res.data;
    },
    onSuccess: (data) => {
      invalidateAll();
      const label = data.status === "active" ? "승인" : "반려";
      const cascaded =
        data.cascaded > 0 ? ` · 전개본 ${data.cascaded}건 동반 전이됨` : "";
      ToastManager.show({ content: `${label} 완료${cascaded}`, duration: 2500 });
    },
    onError: (error) => {
      ToastManager.show({
        content: errorMessage(error, "상태 변경에 실패했습니다."),
        duration: 3000,
      });
    },
  });

  const reextract = useMutation<
    ReextractResult,
    unknown,
    { ruleId: string; fromStep: FromStep }
  >({
    mutationFn: async ({ ruleId, fromStep }) => {
      const res = await doseRuleAdminApi.reextract(ruleId, { fromStep });
      if (res.error !== 0 || !res.data) {
        // 봉투는 HTTP 201 + `{ error, message, data }`다(dose 도메인 관례). 코드·사유를 보존해 던진다.
        throw new ReextractError(res.error, res.message, res.data);
      }
      return res.data;
    },
    onSuccess: (data) => {
      invalidateAll();
      // 표기 출처는 응답 `data.fromStep`이다(화면 설계). 없으면(옛 서버) 표기 생략.
      const suffix =
        data.fromStep === "step2"
          ? " (Step 2부터 · 반영은 잡 완료 후)"
          : data.fromStep === "step3"
          ? " (룰만 · 반영은 잡 완료 후)"
          : " (반영은 잡 완료 후)";
      ToastManager.show({
        content: `재추출 요청됨${suffix} · jobId ${data.jobId}`,
        duration: 3000,
      });
    },
    onError: (error, variables) => {
      const code = (error as { code?: number })?.code;
      const data = (error as { data?: { unavailableReason?: string } | null })?.data;

      // 6112: 보관본 없어 룰만 불가. 사유는 프런트 매핑(서버 message 폴백) + 상세 무효화(D-17).
      if (code === 6112) {
        if (variables?.ruleId) {
          queryClient.invalidateQueries({ queryKey: ["doseRule", variables.ruleId] });
        }
        ToastManager.show({
          content: data?.unavailableReason
            ? reextractReasonText(data.unavailableReason)
            : errorMessage(error, "룰만 재추출을 할 수 없습니다."),
          duration: 3000,
        });
        return;
      }

      // 6102: 같은 품목 잡이 대기·실행 중(서버 message 우선).
      if (code === 6102) {
        ToastManager.show({
          content: errorMessage(error, "이미 재추출 대기 또는 실행 중입니다."),
          duration: 3000,
        });
        return;
      }

      ToastManager.show({
        content: errorMessage(error, "재추출 요청에 실패했습니다."),
        duration: 3000,
      });
    },
  });

  return { updateStatus, reextract };
};
